import * as React from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CompTreeGridV2 from "../../components/treegrid/CompTreeGridV2";
import * as ProcessActions from "../../redux/actions/process.actions";
import * as Actions from "../../redux/actions/user.actions";
import Typography from "../../components/customdesign/Typography";
import { getTransferStoreLabel, rowMatchesTransferDestination, canReassignTasks, prepareGetTasks, countActiveTasks } from "../../utils/Functions";

const isMobile = () => window.innerWidth <= 768;

const ListData = (props) => {
  const navigate  = useNavigate();
  const { defConfig, params, defLang, menuWFL, tasks: resumeTasks } = useSelector((s) => s.Login);
  const {
    componentConfig, requesting, activetasks, tasksheader,
    flowvars, taskPagination, refreshResumeTasks, showTransferStore, transferStoreLabel, transferStoreRemarkable,
  } = useSelector((s) => s.Process);
  const dispatch  = useDispatch();

  const [rows,      setRows]      = React.useState([]);
  const [vars,      setVars]      = React.useState([]);
  const [page,      setPage]      = React.useState(1);
  const [pageSize,  setPageSize]  = React.useState(30);
  const [taskData,  setTaskData]  = React.useState({});
  const [noData,    setNoData]    = React.useState(false);
  const [viewSuspended, setViewSuspended] = React.useState(false); // bandeja: pendientes | suspendidas
  const [tabTotals, setTabTotals] = React.useState({ 4: null, 9: null }); // totales por statusId
  const [badgeVersion, setBadgeVersion] = React.useState(0); // incrementa al suspender/reanudar → refresca ambos badges
  // Búsqueda profunda por valor de variable (endpoint get-active-tasks-by-var):
  // { varvalue, total } o null. La ref evita closures obsoletos en loadTasks.
  const [deepSearch, setDeepSearch] = React.useState(null);
  const deepSearchRef = React.useRef(null);
  const gridRef    = React.useRef(null);
  const hasDataRef = React.useRef(false); // true una vez que hay datos cargados
  const statusId   = viewSuspended ? 9 : 4;

  var { taskId, flowId, officecode: officecodeParam } = useParams();
  const [searchParams] = useSearchParams();
  const transferDest = searchParams.get("transferDest");
  const transferLabel = transferDest ? getTransferStoreLabel(transferDest) : "";
  /* officecode puede venir del path (/getTasks/:officecode/:taskId/:flowId)
     o del query string (?officecode=...) según el origen del menú. */
  const officecode = officecodeParam || searchParams.get("officecode") || null;

  // derive real total from getResumeTasksByUser (already filtered to current user's tasks)
  const resumeEntry = resumeTasks?.find(
    (t) => t.FLOWID === parseInt(flowId) && t.TASKID === parseInt(taskId)
  );
  const resumeTotal = resumeEntry ? parseInt(resumeEntry.TOTAL) : null;

  const pagination = (() => {
    const base = taskPagination || { page, pageSize, total: activetasks?.length || 0, totalPages: 1, hasNext: false, hasPrev: false };
    // Con búsqueda profunda activa el total es el del resultado (no el del
    // badge de la bandeja completa).
    if (deepSearch || viewSuspended || !resumeTotal) return base;
    const totalPages = Math.max(1, Math.ceil(resumeTotal / pageSize));
    return { ...base, total: resumeTotal, totalPages, hasNext: page < totalPages, hasPrev: page > 1 };
  })();

  const loadTasks = (nextPage = page, nextPageSize = pageSize, forceSilent = false) => {
    // Si ya tenemos datos en pantalla: skeleton local, sin loader global.
    // Si es la primera carga: loader completo hasta que lleguen datos.
    const silent = forceSilent || hasDataRef.current;
    if (silent) gridRef.current?.startSkeleton?.();
    const data = { taskId, flowId, page: nextPage, pageSize: nextPageSize, silent, statusId, officecodeFilter: officecode || undefined };
    if (deepSearchRef.current) {
      dispatch(ProcessActions.getTasksByVar({ ...data, varvalue: deepSearchRef.current }));
    } else {
      dispatch(ProcessActions.getTasks(data));
    }
  };

  // Buscador "lupita" de CompTreeGridV2 → búsqueda profunda por variable.
  // Reemplaza la bandeja con los flujos cuya variable runtime tiene ese valor.
  const onDeepSearch = (value) => {
    const v = (value || "").trim();
    if (v.length < 2) return;
    deepSearchRef.current = v;
    setDeepSearch(v);
    if (page !== 1) setPage(1);
    else loadTasks(1, pageSize, hasDataRef.current);
  };

  const exitDeepSearch = () => {
    deepSearchRef.current = null;
    setDeepSearch(null);
    if (page !== 1) setPage(1);
    else loadTasks(1, pageSize, hasDataRef.current);
  };

  const setMessage = async (data) => dispatch(ProcessActions.setMessage(data));

  // "Forzar tarea" (ADMIN/SUPERVISOR): ejecuta la tarea sin pasar por el
  // formulario ni sus validaciones, reusando el mismo flujo redux que usa
  // el envío normal del form (toast, loader global y refresco de la bandeja).
  const forceExecuteTask = (data) => dispatch(ProcessActions.executeTask(data));

  // refreshData: prop que usa CompTreeGridV2 internamente (botón refrescar y
  // tras completar una tarea) para sincronizar Redux. No debe llamar
  // refreshDataLocal (loop). Además, refrescar SALTA la búsqueda profunda:
  // no reenvía el varvalue — la bandeja vuelve a su estado normal.
  const refreshData = () => {
    if (deepSearchRef.current) {
      deepSearchRef.current = null;
      setDeepSearch(null);
    }
    loadTasks(page, pageSize, true);
  };

  // handleHeaderRefresh: botón externo del header — activa el skeleton del grid
  // directamente y luego sincroniza Redux.
  const handleHeaderRefresh = async () => {
    // Igual que refreshData: sale de la búsqueda profunda y recarga la bandeja
    // normal (una sola llamada; el grid se re-sincroniza por Redux).
    if (deepSearchRef.current) {
      deepSearchRef.current = null;
      setDeepSearch(null);
    }
    loadTasks(page, pageSize, true);
  };

  /* ── sync state con redux ── */
  React.useEffect(() => {
    menuWFL.forEach((key) => {
      if (key.FLOWID === parseInt(flowId) && key.TASKID === parseInt(taskId)) {
        setTaskData({ flowname: key.FLOW, taskname: key.TASK });
      }
    });

    if (!requesting) {
      if (!activetasks || activetasks.length === 0) {
        hasDataRef.current = false;
        setNoData(true);
        setRows([]);
        setVars([]);
        setTabTotals((prev) => ({ ...prev, [statusId]: 0 }));
      } else {
        let filteredTasks = activetasks;
        if (transferDest) filteredTasks = filteredTasks.filter((row) => rowMatchesTransferDestination(row, transferDest));
        hasDataRef.current = true;
        setNoData(filteredTasks.length === 0);
        setRows(filteredTasks);
        setVars(flowvars);
        const total = taskPagination?.total ?? activetasks.length;
        setTabTotals((prev) => ({ ...prev, [statusId]: total }));
      }
    } else {
      // Carga no-silenciosa (primera carga): limpiar mientras llega.
      // Cargas silenciosas no tocan requesting, por lo que este bloque no se ejecuta.
      setNoData(false);
      setRows([]);
      setVars([]);
    }

    if (refreshResumeTasks && !requesting) {
      dispatch(Actions.getResumeTasksByUser());
      loadTasks(page, pageSize);
    }
  }, [activetasks, flowvars, requesting, refreshResumeTasks, transferDest, officecode]);

  React.useEffect(() => {
    hasDataRef.current = false; // nueva tarea / cambio de bandeja / cambio de oficina → loader completo
    setRows([]);
    setPage(1);
  }, [taskId, flowId, viewSuspended, officecode]);
  React.useEffect(() => { loadTasks(page, pageSize); }, [taskId, flowId, page, pageSize, transferDest, viewSuspended, officecode]);

  // Precarga y refresco de badges de ambas bandejas con una sola llamada al backend.
  React.useEffect(() => {
    if (!taskId || !flowId) return;
    let cancelled = false;
    countActiveTasks({ taskId, flowId, officecodeFilter: officecode || undefined })
      .then((resp) => {
        if (cancelled) return;
        if (resp?.status) {
          setTabTotals({ 4: resp.pending, 9: resp.suspended });
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [taskId, flowId, officecode, badgeVersion]);

  const filterGridRows = React.useCallback(
    (nextRows) => {
      let result = nextRows || [];
      if (transferDest) result = result.filter((row) => rowMatchesTransferDestination(row, transferDest));
      return result;
    },
    [transferDest]
  );

  return (
    <div className="xflow-task-page" style={{ marginLeft: -15, marginRight: -15 }}>

      {/* ── Header ── */}
      <div className="xflow-task-page-header">
        <div>
          <span className="xflow-kicker">{defConfig[defLang]["totaltaskslabel"]}</span>
          <Typography
            variant="h3"
            mt={isMobile() ? 0 : 1}
            ml={isMobile() ? 1 : 0}
            mb="0" pl="1"
            style={{ textTransform: "capitalize" }}
          >
            <span className="e-component-title e-btn-icon e-icons e-group-2 e-icon-left"></span>
            <span className="e-component-title"> {taskData.flowname}</span>
          </Typography>
        </div>
        {isMobile() && (
          <button
            type="button"
            className="xft-icon-btn"
            style={{ position: "absolute", right: 12, top: 15 }}
            onClick={handleHeaderRefresh}
          >
            <span className="e-btn-icon e-icons e-refresh-2"></span>
          </button>
        )}
      </div>

      <Typography
        variant="h5" mt={1} mb="1" pl="1"
        ml={isMobile() ? 1 : 0}
        style={{ textTransform: "capitalize" }}
      >
        <span>{taskData.taskname}</span>
        {transferLabel && (
          <span className="xflow-transfer-route-chip">{transferLabel}</span>
        )}
        {officecode && (
          <span className="xflow-transfer-route-chip">{officecode}</span>
        )}
        {deepSearch && (
          <span className="xflow-deepsearch-chip">
            <span className="e-icons e-search"></span>
            Búsqueda: "<b>{deepSearch}</b>" · {pagination.total || 0} resultado{pagination.total === 1 ? "" : "s"}
            <button
              type="button"
              className="xflow-deepsearch-chip-x"
              onClick={exitDeepSearch}
              title="Salir de la búsqueda profunda y volver a la bandeja"
            >
              <span className="e-icons e-close"></span>
            </button>
          </span>
        )}
      </Typography>

      {/* ── Toggle Pendientes / Suspendidas (solo ADMIN/SUPERVISOR) ── */}
      {canReassignTasks() && (
        <div className="xft-view-toggle">
          <button
            type="button"
            className={"xft-view-tab" + (!viewSuspended ? " xft-view-tab--active" : "")}
            onClick={() => setViewSuspended(false)}
          >
            {defConfig[defLang].pendinglabel || "Pendientes"}
            {tabTotals[4] > 0 && <span className="xft-view-tab-badge">{tabTotals[4]}</span>}
          </button>
          <button
            type="button"
            className={"xft-view-tab" + (viewSuspended ? " xft-view-tab--active" : "")}
            onClick={() => setViewSuspended(true)}
          >
            <span className="e-icons e-pause" style={{ fontSize: 12, marginRight: 5 }}></span>
            {defConfig[defLang].suspendedlabel || "Suspendidas"}
            {tabTotals[9] > 0 && <span className="xft-view-tab-badge">{tabTotals[9]}</span>}
          </button>
        </div>
      )}

      {/* ── Tabla con datos ── */}
      {rows && rows.length > 0 && (
        <div className="xflow-task-list-panel">

          {/* Paginación server-side */}
          <div className="xflow-server-pagination">
            <Typography variant="h6" mb={0} ml={isMobile() ? 1 : 0} pl="1">
              {defConfig[defLang]["totaltaskslabel"]}:{" "}
              {transferDest
                ? rows.length
                : (pagination.totalPages || 1) <= 1
                  ? (activetasks?.length || 0)
                  : (pagination.total || activetasks?.length || 0)}
            </Typography>

            <div className="xflow-server-pagination-controls">
              <button
                type="button"
                className="xflow-page-button"
                disabled={!pagination.hasPrev && page <= 1}
                onClick={() => setPage(Math.max(1, page - 1))}
              >
                <span className="e-icons e-chevron-left"></span>
              </button>

              <span className="xflow-page-indicator">
                {pagination.page || page} / {pagination.totalPages || 1}
              </span>

              <button
                type="button"
                className="xflow-page-button"
                disabled={!pagination.hasNext && page >= (pagination.totalPages || 1)}
                onClick={() => setPage(page + 1)}
              >
                <span className="e-icons e-chevron-right"></span>
              </button>

              <select
                className="xflow-page-size-select"
                value={pageSize}
                onChange={(e) => { setPage(1); setPageSize(parseInt(e.target.value, 10)); }}
              >
                <option value={15}>15</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
          </div>

          {/* Grid de tareas — sin Syncfusion */}
          {rows.length > 0 && tasksheader?.length > 0 && (
            <CompTreeGridV2
              ref={gridRef}
              setMessage={setMessage}
              refreshData={refreshData}
              forceExecuteTask={forceExecuteTask}
              onSuspendResume={() => setBadgeVersion(v => v + 1)}
              defConfig={defConfig}
              defLang={defLang}
              taskname={rows[0].taskname}
              tasktypeId={rows[0].tasktypeId}
              taskId={taskId}
              flowId={flowId}
              statusId={statusId}
              suspendedMode={viewSuspended}
              header={tasksheader}
              dataSource={rows}
              vars={vars}
              showTransferStore={showTransferStore}
              transferStoreLabel={transferStoreLabel}
              transferStoreRemarkable={transferStoreRemarkable}
              filterRows={filterGridRows}
              onDeepSearch={onDeepSearch}
              name="activetasks"
              serverPagination={pagination}
            />
          )}
        </div>
      )}

      {/* ── Sin datos ── */}
      {noData && (
        <div className="xft-no-data-page">
          <span className="e-icons e-check-box xft-no-data-icon"></span>
          <p className="xft-no-data-text">
            {deepSearch
              ? `Sin resultados para "${deepSearch}"`
              : defConfig[defLang].nopendings || "Sin tareas pendientes"}
          </p>
        </div>
      )}
    </div>
  );
};

export default ListData;
